import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { useAppSelector } from '../../../redux/hooks';
import styles from './Graph.module.scss';
import { CurrencyInputFormatter } from '../../../utils/currencyFormatter';
import { useTranslation } from 'react-i18next';
import { NumberFormatter } from '../../../utils/NumberFormatter';
interface LineAndBarChartProps {
  lineChartData: LineChartData;
  barChartData: any[];
  activationPeriod?: any;
  isActivartionRate?: boolean;
  currency?: string;
  kpiCode?: any;
  affiliateCode?: any;
  truncateDecimals?: boolean;
}

const Graph: React.FC<LineAndBarChartProps> = ({
  lineChartData,
  barChartData,
  activationPeriod,
  isActivartionRate,
  currency,
  kpiCode,
  affiliateCode = '010',
  truncateDecimals = false,
}) => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const isExpanded = useAppSelector((state: any) => state.sideNavExpansion.isExpanded);
  const [showLineChart, setShowLineChart] = useState(true);
  const currentYearData = lineChartData?.body?.lines[0];
  const lastDatapoint = currentYearData?.datapoint[currentYearData?.datapoint?.length - 1];
  const currentValue = lastDatapoint ? parseFloat(lastDatapoint?.yValue) : null;
  const currentMonth = lastDatapoint ? t(`chartmonths.${lastDatapoint.xValue}`) : undefined;
  useEffect(() => {
    if (activationPeriod) {
      setShowLineChart(true);
    }
  }, [activationPeriod]);

  useEffect(() => {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 400);
  }, [isExpanded]);

  const getChartData = () => {
    return lineChartData?.body?.lines?.map((line, index) => {
      const dataPointMap = new Map(line?.datapoint.map((point) => [point.xValue, point.yValue]));

      const chartData = lineChartData?.xAxis.map((x) => {
        const yValue = dataPointMap.get(x.dataValue);
        if (yValue === undefined) return null;

        return +yValue || 0;
      });

      return {
        name: line?.lineName,
        data: chartData,
        dashArray: index === 0 ? 0 : 8,
      };
    });
  };
  const formattedLineChartData = getChartData();
  const xAxisCategories = lineChartData?.xAxis.map((x) => t(`chartmonths.${x.dataValue}`));
  if (currentMonth && !xAxisCategories.includes(currentMonth)) {
    xAxisCategories.push(currentMonth);
  }
  const xAxisLabelColors = Array.from(new Set(xAxisCategories)).map((category) =>
    category === currentMonth
      ? currentTheme === 'dark'
        ? '#99B2F5'
        : '#5969D6'
      : currentTheme === 'dark'
        ? '#AEAEB2'
        : '#707070'
  );
  const yAxisDataType = lineChartData?.yAxis?.[0]?.dataType || 'number';
  const yAxisDataValue = lineChartData?.yAxis;
  const dataValues = yAxisDataValue.map((item) => item.dataValue);
  const formatYAxisLabel = (value: string | number | undefined): string => {
    if (value === undefined || value === null) {
      return 'N/A';
    }
    switch (yAxisDataType) {
      case 'percentage':
        return `${NumberFormatter(affiliateCode, +value, truncateDecimals) ?? value}%`;
      case 'currency':
        return `${CurrencyInputFormatter(String(currency), Number(value), truncateDecimals).replace(/\.00$/, '')}`;
      case 'number': {
        const numb = typeof value === 'string' ? parseInt(value) : Math.trunc(+value);
        return NumberFormatter(affiliateCode ?? '010', Math.abs(numb), truncateDecimals) ?? value.toString();
      }
      case 'string':
        return value.toString();
      default:
        return value.toString();
    }
  };
  const lineColors = currentTheme === 'dark' ? ['#99B2F5', '#D8D8D9'] : ['#5969D6', '#5E5E5E'];
  const chartOptions: ApexOptions = {
    chart: {
      parentHeightOffset: 0,
      offsetX: -10,
      height: 235,
      type: 'line',
      toolbar: {
        show: false,
      },
      background: 'transparent',
      zoom: {
        enabled: false,
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 100,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
    },
    colors: lineColors,
    stroke: {
      curve: 'smooth',
      width: [2, 2],
      dashArray: [0, 8],
    },
    markers: {
      size: 4,
      colors: lineColors,
      strokeColors: lineColors,
      strokeWidth: 0,
    },
    xaxis: {
      categories: xAxisCategories,
      labels: {
        style: {
          colors: xAxisLabelColors,
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      title: {
        text: showLineChart ? '' : 'Revenue ($K)',
        style: {
          color: currentTheme === 'dark' ? '#AEAEB2' : '#707070',
        },
      },
      // stacked: true,
      labels: {
        style: {
          colors: currentTheme === 'dark' ? '#AEAEB2' : '#707070',
          fontSize: '12px',
        },
        formatter: (value: number): string => formatYAxisLabel(value),
      },
      min: Math.min(...dataValues),
      max: Math.max(...dataValues),
      tickAmount: yAxisDataValue?.length - 1,
    },
    grid: {
      borderColor: currentTheme === 'dark' ? '#636366' : '#E4E4E4',
      padding: {
        right: 20,
      },
    },
    dataLabels: {
      enabled: false,
    },
    annotations: {
      xaxis: [
        {
          x: currentMonth,
          borderColor: currentTheme === 'dark' ? '#D6D6D6' : '#E4E4E4',
          strokeDashArray: 5,
          label: {
            style: {
              color: '#fff',
              background: 'red',
            },
            text: '',
          },
        },
      ],
      points: [
        {
          x: currentMonth,
          y: currentValue,
          marker: {
            size: 10,
            fillColor: currentTheme === 'dark' ? '#99B2F5' : '#5969D6',
            strokeColor:
              currentTheme === 'dark' ? 'rgba(153, 178, 245, 0.36)' : 'rgba(89, 105, 214, 0.36)',
            strokeWidth: 8,
            // ignore_pointer_events: Allows the hover event to pass through the annotation marker
            // to the actual chart series data point underneath, enabling the tooltip to show.
            cssClass: styles.ignore_pointer_events,
          },
          label: {
            text: '',
            style: {
              color: currentTheme === 'dark' ? '#99B2F5' : '#5969D6',
              fontSize: '12px',
              background: 'transparent',
            },
          },
        },
      ],
    },
    tooltip: {
      enabled: true,
      shared: false,
      intersect: true,
      theme: currentTheme,
      style: {
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
      },
      fillSeriesColor: true,
      y: {
        formatter: (value) => formatYAxisLabel(value),
        title: {
          formatter: (seriesName) => `${seriesName}: `,
        },
      },
      marker: {
        show: true,
      },
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const month = xAxisCategories[dataPointIndex];
        const seriesName = w.globals.seriesNames[seriesIndex];
        const val = series[seriesIndex][dataPointIndex];

        // Use original month data (always English) for fiscal year check
        const originalMonth = lineChartData?.xAxis?.[dataPointIndex]?.dataValue;
        const isFiscalYearMonth = ['Sep', 'Oct', 'Nov', 'Dec'].includes(originalMonth);

        // Extract year from series name (e.g., "2025" or "2024")
        const yearValue = parseInt(seriesName, 10);
        const adjustedYearValue = isFiscalYearMonth ? yearValue - 1 : yearValue;

        let sameValueSeries = false;
        let seriesFirst = null;
        let seriesSecond = null;
        if (
          seriesIndex < 2 &&
          series[1] &&
          series[0][dataPointIndex] !== undefined &&
          series[1][dataPointIndex] !== undefined
        ) {
          sameValueSeries = series[0][dataPointIndex] === series[1][dataPointIndex];
          seriesFirst = w?.globals?.seriesNames?.[0] || 'Series 1';
          seriesSecond = w?.globals?.seriesNames?.[1] || 'Series 2';
        }

        let content = `
          <div style="
            padding: 12px;
            background: ${lineColors[seriesIndex] || lineColors[0]};
            color: ${currentTheme === 'dark' ? '#000000' : '#F4F4F4'};
            border-radius: 6px;
            min-height: 60px;
            width: auto;
            display: flex;
            align-items: center;
            flex-direction: column;
            justify-content: space-between;
            font-size: 14px;
            line-height: 18px;
          ">`;

        if (!sameValueSeries) {
          content += `
            <div style="margin-bottom: 4px;">${adjustedYearValue} ${month}</div>
            <div>${t('value')}: ${formatYAxisLabel(val)}</div>`;
        } else {
          const firstYear = parseInt(seriesFirst, 10) || new Date().getFullYear();
          const secondYear = parseInt(seriesSecond, 10) || new Date().getFullYear() - 1;
          const adjustedFirstYear = isFiscalYearMonth ? firstYear - 1 : firstYear;
          const adjustedSecondYear = isFiscalYearMonth ? secondYear - 1 : secondYear;
          content += `
            <div style="margin-bottom: 4px;">${adjustedFirstYear} ${month} : ${formatYAxisLabel(series[0][dataPointIndex])}</div>
            <div style="margin-bottom: 4px;">${adjustedSecondYear} ${month} : ${formatYAxisLabel(series[1][dataPointIndex])}</div>`;
        }

        content += `</div>`;

        return content;
      },

      hideEmptySeries: true,
    },
    legend: {
      show: false,
    },
  };
  return (
    <div className={styles.chart_container} data-theme={currentTheme}>
      <Chart
        options={chartOptions}
        series={showLineChart ? formattedLineChartData : barChartData}
        type={showLineChart ? 'line' : 'bar'}
        height={235}
      />
    </div>
  );
};

export default Graph;
